import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Snackbar,
    Grid,
    Card,
    CardContent,
    Divider,
} from '@mui/material';

const Profile = ({ api, isAuthenticated }) => {
    const [store, setStore] = useState(null);
    const [storeDetails, setStoreDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [updating, setUpdating] = useState(false);
    const [billingUpdating, setBillingUpdating] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const showSnackbar = (message, severity = 'success') =>
        setSnackbar({ open: true, message, severity });

    useEffect(() => {
        if (isAuthenticated) {
            fetchStoreDetails();
        }
    }, [isAuthenticated]);

    const fetchStoreDetails = () => {
        setLoading(true);
        api.get('/api/store')
            .then(response => {
                setStore(response.data.details);
                setStoreDetails(response.data.store);
                setLoading(false);
            })
            .catch(error => {
                setError('Failed to fetch store details');
                setLoading(false);
            });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setStore(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = () => {
        setUpdating(true);
        api.put('/api/store', store)
            .then(() => {
                showSnackbar('Store details updated successfully!');
                setUpdating(false);
            })
            .catch(error => {
                setError('Failed to update store details');
                setUpdating(false);
            });
    };

    const handleBillingUpdate = () => {
        setBillingUpdating(true);
        api.post('/api/billing/update', { store_id: store.store_id })
            .then(() => {
                window.location.href = '/billing';
                setBillingUpdating(false);
            })
            .catch(error => {
                setError('Failed to update trial');
                setBillingUpdating(false);
            });
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
    if (!store) return null;

    return (
        <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom fontWeight={700}>Profile</Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Billing Details</Typography>
                            <Divider sx={{ mb: 2 }} />
                            <TextField label="Billing Name" name="billing_name" value={store.billing_name || ''} onChange={handleChange} fullWidth margin="normal" size="small" />
                            <TextField label="Billing Email" name="billing_email" value={store.billing_email || ''} onChange={handleChange} fullWidth margin="normal" size="small" />
                            <TextField label="Billing Phone" name="billing_phone" value={store.billing_phone || ''} onChange={handleChange} fullWidth margin="normal" size="small" />
                            <TextField label="Billing Address" name="billing_address" value={store.billing_address || ''} onChange={handleChange} fullWidth margin="normal" size="small" />
                            <TextField label="Billing Country" name="billing_country" value={store.billing_country || ''} onChange={handleChange} fullWidth margin="normal" size="small" />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Store Information</Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Store ID</Typography>
                                    <Typography variant="body2" fontWeight={500}>{store.store_id}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Trial</Typography>
                                    <Typography variant="body2" fontWeight={500}>{store.trial ? 'Yes' : 'No'}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Billing Date</Typography>
                                    <Typography variant="body2" fontWeight={500}>{store.billing_date}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Store URL</Typography>
                                    <Typography variant="body2" fontWeight={500}>{storeDetails?.generalInfo?.storeUrl}</Typography>
                                </Box>
                            </Box>
                            {store.trial && (
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleBillingUpdate}
                                    disabled={billingUpdating}
                                    fullWidth
                                    sx={{ mt: 3 }}
                                >
                                    {billingUpdating ? <CircularProgress size={20} color="inherit" /> : 'Update Trial'}
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Button variant="contained" color="primary" onClick={handleSave} disabled={updating} sx={{ mt: 3 }}>
                {updating ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
            </Button>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Profile;
